import { AnalysisFeedbackListDrawerHeader } from '@modules/common/components/AnalysisFeedbackListDrawerHeader';
import { CircularLoader } from '@modules/common/components/CircularLoader';
import { LAST_5_YEARS } from '@modules/common/constants';
import { FeedbackListViewType } from '@modules/feedback/constants';
import { useGetSelectedFeedbackMetadata } from '@modules/feedback/hooks';
import { FeedbackListStoreProvider } from '@modules/feedback/store/useFeedbackListStore';
import { FeedbackList } from '@modules/feedbackV2/common/components/FeedbackList';
import {
  getDurationFromAssistantResponseEqlQuery,
  getWhereClauseFromAssistantResponseEqlQuery,
} from '@modules/oracle/utils';
import { SavedFeedbackListActionType } from '@modules/savedFeedbackList/store/savedFeedbackListActionType';
import { useSavedFeedbackListStore } from '@modules/savedFeedbackList/store/useSavedFeedbackListStore';
import { Box, Paper, Popover, Stack } from '@mui/material';
import {
  AnalysisUseCase,
  AnalysisViewFragment,
  TimeUnit,
} from 'generated/graphql';
import { useMemo, useState } from 'react';
import { TimeRange } from 'v2/core/entities';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { useGetUser } from 'v2/core/services/user';
import { ArtefactCitationFeedbackListPopoverHeaderActions } from '../ArtefactCitationFeedbackListPopoverHeaderActions';

interface ArtefactCitationFeedbackListPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  analysis: AnalysisViewFragment | undefined;
  quoteContextClaim: string | undefined;
  anchorEl: HTMLElement | null;
}

export const ArtefactCitationFeedbackListPopover = ({
  isOpen,
  onClose,
  analysis,
  quoteContextClaim,
  anchorEl,
}: ArtefactCitationFeedbackListPopoverProps) => {
  const [totalRecordCount, setTotalRecordCount] = useState<number>();
  const { data: sourceDetailsMap } = useGetSourceDetailsMap();
  const {
    tenant: { timezone },
  } = useGetUser();

  const { savedFeedbackIDs: feedbackIDsOfCollection, dispatch } =
    useSavedFeedbackListStore();

  const queryContainer = useMemo(
    () => ({
      AggregatePeriod: TimeUnit.Week,
      DateRange:
        analysis?.context.eql &&
        analysis?.useCase !== AnalysisUseCase.Collection
          ? (getDurationFromAssistantResponseEqlQuery(
              analysis.context.eql.properties.duration,
            ) as TimeRange)
          : (LAST_5_YEARS as TimeRange),
      WhereClause:
        getWhereClauseFromAssistantResponseEqlQuery({
          whereClause: analysis?.context.eql?.properties.whereClause,
          timezone,
          sourceDetailsMap,
        }) ?? EmptyWhereClause,
    }),
    [analysis?.context.eql, analysis?.useCase, timezone, sourceDetailsMap],
  );

  const selectedFeedbackMetadata = useGetSelectedFeedbackMetadata({
    whereClause: queryContainer?.WhereClause,
  });

  return (
    <Popover
      id="citation-feedback-list-popover"
      open={isOpen}
      anchorEl={anchorEl}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer,
      }}
      anchorOrigin={{
        vertical: 'center',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      onClose={() => {
        dispatch({ type: SavedFeedbackListActionType.RESET });
        onClose();
      }}
      PaperProps={{ elevation: 4, sx: { width: 600 } }}
      disableRestoreFocus
    >
      {!analysis ? (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          width="100%"
          py={10}
        >
          <CircularLoader size={50} />
        </Stack>
      ) : (
        <>
          <Stack
            spacing={2}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            p={2}
            component={Paper}
            elevation={0}
          >
            <Box sx={{ width: '60%' }}>
              <AnalysisFeedbackListDrawerHeader
                useCase={analysis.useCase}
                feedbackCount={totalRecordCount}
                createdBy={`${analysis.createdBy.firstName} ${analysis.createdBy.lastName}`}
                title={analysis.title}
              />
            </Box>
            <ArtefactCitationFeedbackListPopoverHeaderActions
              analysis={analysis}
            />
          </Stack>
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            <FeedbackListStoreProvider>
              <FeedbackList
                selectedFeedbackMetadata={selectedFeedbackMetadata}
                queryContainer={queryContainer}
                isSummaryView={false}
                onFirstPageFetch={(resp) => setTotalRecordCount(resp.hits)}
                onlyShowFeedbackIDs={feedbackIDsOfCollection}
                feedbackListViewType={
                  analysis?.useCase === AnalysisUseCase.Collection
                    ? FeedbackListViewType.SAVED
                    : FeedbackListViewType.DEFAULT
                }
                quoteContextClaim={quoteContextClaim}
                shouldHidePredictionsInCollapsedView
              />
            </FeedbackListStoreProvider>
          </Box>
        </>
      )}
    </Popover>
  );
};
